import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ChangeEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  confirmExplainPhoto,
  deleteExplainPhotoByToken,
  getExplainPhotoUploadUrl,
  listExplainPhotosByToken,
  uploadImageToS3,
  type ExplainPhoto,
} from '@/api/lcLearning'
import { getApiErrorMessage } from '@/utils/apiError'
import { getExplainVideoCapability, type ExplainVideoCapability } from '@/api/lcExplainVideos'
import { ExplainVideoPanel, getRecoverableVideoCapability } from '@/components/photo/ExplainVideoPanel'

const MAX_PHOTOS = 20
const MAX_FILE_BYTES = 10 * 1024 * 1024
const CONTENT_TYPE_BY_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
}
const ALLOWED_CONTENT_TYPES = new Set(Object.values(CONTENT_TYPE_BY_EXTENSION))

function resolveContentType(file: File): string | null {
  const declared = file.type.toLowerCase()
  if (ALLOWED_CONTENT_TYPES.has(declared)) return declared
  if (declared) return null
  const extension = file.name.split('.').pop()?.toLowerCase()
  return extension ? CONTENT_TYPE_BY_EXTENSION[extension] ?? null : null
}

/**
 * 설명하기(빌드업 STEP4) 사진 업로드 — **비로그인 모바일 페이지** (Phase 4c, §5-8=A).
 *
 * 학습자 LMS 가 표시한 QR 을 휴대폰으로 스캔하면 이 페이지(`/photo-upload?token=...`)로 진입한다.
 * 로그인 없이 토큰만으로 카메라/파일 사진을 presigned 업로드한다. 공개 라우트(ProtectedRoute 밖).
 */
export default function PhotoUploadPage() {
  const { t } = useTranslation('learner')
  const { t: videoT } = useTranslation('video')
  const [params] = useSearchParams()
  const token = params.get('token')
  const requestedMode = params.get('mode')

  const [photos, setPhotos] = useState<ExplainPhoto[]>([])
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [uploading, setUploading] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [videoCapability, setVideoCapability] = useState<ExplainVideoCapability | null>(() => token ? getRecoverableVideoCapability(token) : null)
  const [mode, setMode] = useState<'photo' | 'video'>('photo')
  const cameraRef = useRef<HTMLInputElement>(null)
  const galleryRef = useRef<HTMLInputElement>(null)

  const reload = useCallback(() => {
    if (!token) return
    listExplainPhotosByToken(token).then(setPhotos).catch(() => {})
  }, [token])

  useEffect(() => {
    reload()
  }, [reload])

  useEffect(() => {
    if (!token) return
    let disposed = false
    getExplainVideoCapability(token).then(capability => {
      if (!disposed) {
        setVideoCapability(capability)
        setMode(capability.enabled && requestedMode === 'video' ? 'video' : 'photo')
      }
    }).catch(() => { if (!disposed) setVideoCapability(getRecoverableVideoCapability(token)) })
    return () => { disposed = true }
  }, [token, requestedMode])

  const pendingPreviews = useMemo(
    () => pendingFiles.map((file) => ({ file, url: URL.createObjectURL(file) })),
    [pendingFiles],
  )

  useEffect(
    () => () => pendingPreviews.forEach((preview) => URL.revokeObjectURL(preview.url)),
    [pendingPreviews],
  )

  const onPick = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const selected = Array.from(e.target.files ?? [])
      e.target.value = '' // 같은 파일 재선택 허용
      if (selected.length === 0 || !token) return
      setError(null)
      const remaining = Math.max(0, MAX_PHOTOS - photos.length - pendingFiles.length)
      const files = selected.slice(0, remaining)
      const validFiles = files.filter((file) => resolveContentType(file) && file.size <= MAX_FILE_BYTES)
      const oversized = files.some((file) => file.size > MAX_FILE_BYTES)
      const unsupported = files.some((file) => !resolveContentType(file))
      setPendingFiles((current) => [...current, ...validFiles])
      if (selected.length > files.length) setError(t('photoUpload.selectionLimit', { count: remaining }))
      else if (oversized) setError(t('photoUpload.fileTooLarge'))
      else if (unsupported) setError(t('photoUpload.unsupportedType'))
    },
    [token, photos.length, pendingFiles.length, t],
  )

  const uploadPending = useCallback(async () => {
    if (!token || pendingFiles.length === 0 || uploading) return
    setUploading(true)
    setError(null)
    let failed = 0
    const uploaded: ExplainPhoto[] = []
    const failedFiles: File[] = []
    try {
      for (const file of pendingFiles) {
        const contentType = resolveContentType(file)
        if (!contentType) continue
        try {
          const { uploadUrl, fileUrl } = await getExplainPhotoUploadUrl(token, contentType)
          await uploadImageToS3(uploadUrl, file, contentType)
          uploaded.push(await confirmExplainPhoto(token, fileUrl))
        } catch (err) {
          failed += 1
          failedFiles.push(file)
          setError(getApiErrorMessage(err, t))
        }
      }
      setPendingFiles(failedFiles)
      if (uploaded.length > 0) {
        setPhotos((current) => [...current, ...uploaded])
        reload()
      }
      if (failed > 0) setError(t('photoUpload.partialFailure', { count: failed }))
    } finally {
      setUploading(false)
    }
  }, [pendingFiles, reload, t, token, uploading])

  const deleteUploaded = useCallback(async (photoId: number) => {
    if (!token || deletingId != null) return
    setDeletingId(photoId)
    setError(null)
    try {
      await deleteExplainPhotoByToken(token, photoId)
      setPhotos((current) => current.filter((photo) => photo.id !== photoId))
    } catch (err) {
      setError(getApiErrorMessage(err, t))
    } finally {
      setDeletingId(null)
    }
  }, [deletingId, t, token])

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6 text-center">
        <p role="alert" className="text-sm text-red-600">{t('photoUpload.noToken')}</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 px-5 py-8">
      <div className="mx-auto flex max-w-md flex-col gap-5">
        <header className="text-center">
          <h1 className="text-xl font-bold text-gray-900">{t('photoUpload.title')}</h1>
          <p className="mt-1 text-sm text-gray-600">{t('photoUpload.hint')}</p>
        </header>

        {videoCapability?.enabled ? <div role="tablist" aria-label={videoT('attachmentType')} className="grid grid-cols-2 rounded-xl border border-gray-200 bg-white p-1">
          {(['photo', 'video'] as const).map(value => <button key={value} type="button" role="tab" aria-selected={mode === value} onClick={() => setMode(value)} className={`min-h-11 rounded-lg px-3 py-2 text-sm font-semibold ${mode === value ? 'bg-primary-600 text-white' : 'text-gray-600'}`}>{videoT(value)}</button>)}
        </div> : null}

        {videoCapability?.enabled ? <ExplainVideoPanel key={token} token={token} active={mode === 'video'} initialCapability={videoCapability} /> : null}

        {mode === 'photo' ? <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => cameraRef.current?.click()}
            disabled={uploading || photos.length + pendingFiles.length >= MAX_PHOTOS}
            className="rounded-xl bg-primary-600 px-3 py-4 text-base font-semibold text-white hover:bg-primary-700 disabled:bg-gray-300"
          >
            {uploading ? t('photoUpload.uploading') : t('photoUpload.camera')}
          </button>
          <button
            type="button"
            onClick={() => galleryRef.current?.click()}
            disabled={uploading || photos.length + pendingFiles.length >= MAX_PHOTOS}
            className="rounded-xl border border-primary-600 bg-white px-3 py-4 text-base font-semibold text-primary-700 hover:bg-primary-50 disabled:border-gray-300 disabled:text-gray-400"
          >
            {uploading ? t('photoUpload.uploading') : t('photoUpload.gallery')}
          </button>
        </div> : null}
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={onPick}
        />
        <input
          ref={galleryRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={onPick}
        />

        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}

        {pendingPreviews.length > 0 && (
          <section aria-label={t('photoUpload.selected')}>
            <div className="mb-2 flex items-center justify-between gap-3">
              <h2 className="text-sm font-semibold text-gray-700">{t('photoUpload.selected')}</h2>
              <button
                type="button"
                onClick={uploadPending}
                disabled={uploading}
                className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-bold text-white disabled:bg-gray-300"
              >
                {uploading ? t('photoUpload.uploading') : t('photoUpload.upload')}
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {pendingPreviews.map((preview, index) => (
                <div key={`${preview.file.name}-${preview.file.lastModified}-${index}`} className="relative">
                  <img src={preview.url} alt="" className="aspect-square w-full rounded-lg border border-gray-200 object-cover" />
                  <button
                    type="button"
                    aria-label={t('photoUpload.removeSelected', { name: preview.file.name })}
                    onClick={() => setPendingFiles((current) => current.filter((_, fileIndex) => fileIndex !== index))}
                    className="absolute left-1 top-1 flex size-7 items-center justify-center rounded-full bg-black/70 text-lg font-bold text-white"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        <section aria-label={t('photoUpload.uploaded')}>
          <h2 className="mb-2 text-sm font-semibold text-gray-700">
            {t('photoUpload.uploaded')} ({photos.length} / {MAX_PHOTOS})
          </h2>
          <p className="mb-2 text-xs text-gray-500">
            {t('photoUpload.remaining', { count: MAX_PHOTOS - photos.length - pendingFiles.length })}
          </p>
          {photos.length === 0 ? (
            <p className="text-sm text-gray-400">{t('photoUpload.empty')}</p>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {photos.map((p) => (
                <div key={p.id} className="relative">
                  <img src={p.photoUrl} alt="" className="aspect-square w-full rounded-lg border border-gray-200 object-cover" />
                  <button
                    type="button"
                    aria-label={t('photoUpload.deleteUploaded', { no: p.ordinal })}
                    disabled={deletingId === p.id}
                    onClick={() => deleteUploaded(p.id)}
                    className="absolute left-1 top-1 flex size-7 items-center justify-center rounded-full bg-black/70 text-lg font-bold text-white disabled:opacity-50"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
